import { html, nothing } from "lit";

export type ChatHydrationMode = "idle" | "warm" | "cold";

/**
 * Cover the Fleet Control transcript pane with the legacy centered
 * spinner. Warm cache hits, the outgoing crossfade shell, and cold
 * hydration (which renders a skeleton + progress hairline instead)
 * all return `false` — the pane is never blanked or hidden.
 */
export function shouldShowChatTranscriptOverlay(params: {
  transcriptLoading: boolean;
  historyLoading: boolean;
  hasTranscriptItems: boolean;
  hasOutgoingTranscript?: boolean;
  hydrationMode?: ChatHydrationMode;
}): boolean {
  if (params.hasTranscriptItems || params.hasOutgoingTranscript) {
    return false;
  }
  if (params.hydrationMode === "cold") {
    return false;
  }
  if (params.transcriptLoading) {
    return true;
  }
  return params.historyLoading && !params.hasTranscriptItems;
}

export function shouldShowTranscriptSkeleton(params: {
  historyLoading: boolean;
  hasTranscriptItems: boolean;
  hydrationMode?: ChatHydrationMode;
}): boolean {
  return params.hydrationMode === "cold" && params.historyLoading && !params.hasTranscriptItems;
}

export function shouldShowTranscriptProgressHairline(params: {
  historyLoading: boolean;
  hydrationMode?: ChatHydrationMode;
}): boolean {
  if (!params.historyLoading) {
    return false;
  }
  const mode = params.hydrationMode ?? "idle";
  return mode !== "idle";
}

export function renderChatTranscriptLoadingOverlay() {
  return html`
    <div
      class="chat-transcript-loading-overlay"
      data-testid="chat-transcript-loading-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading chat transcript"
    >
      <span class="chat-transcript-loading-overlay__icon" aria-hidden="true">…</span>
    </div>
  `;
}

export function renderChatTranscriptSkeleton() {
  return html`
    <div
      class="chat-transcript-skeleton"
      data-testid="chat-transcript-skeleton"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading chat transcript"
    >
      <div class="chat-transcript-skeleton__row chat-transcript-skeleton__row--assistant"></div>
      <div class="chat-transcript-skeleton__row chat-transcript-skeleton__row--user"></div>
      <div class="chat-transcript-skeleton__row chat-transcript-skeleton__row--assistant"></div>
      <div
        class="chat-transcript-skeleton__row chat-transcript-skeleton__row--assistant chat-transcript-skeleton__row--code"
      ></div>
    </div>
  `;
}

export function renderChatTranscriptProgressHairline(params?: {
  error?: string | null;
  onRetry?: () => void;
}) {
  const error = params?.error?.trim();
  if (error) {
    return html`
      <div class="chat-transcript-hairline chat-transcript-hairline--error" role="alert">
        <span>${error}</span>
        ${
          params?.onRetry
            ? html`<button type="button" class="btn btn--sm" @click=${params.onRetry}>
                Retry
              </button>`
            : nothing
        }
      </div>
    `;
  }
  return html`
    <div class="chat-transcript-hairline" aria-hidden="true">
      <span class="chat-transcript-hairline__bar"></span>
    </div>
  `;
}
